"use client";

import { FC } from "react";
import cx from "classnames";
import styles from "./offer-panel.module.scss";
import { TransitStatus, Vehicle, VehicleType } from "../../../types/vehicle";
import { ClientSiteData } from "../../../types/site";
import BackButton from "../../common-components/back-button/back-button";
import CarFavoriteButton from "../../common-components/vehicle-card/car-favorite-button";
import CarCompareCheckbox from "../../common-components/vehicle-card/car-compare-checkbox";
import { getTranslationModule, Translations } from "../../../utils/translations";
import { useIsMobile } from "@/utils/useIsMobile";
import { useLikedVehicles } from "@/hooks/useLikes";
import { useCompareVehicles } from "@/hooks/useCompares";
import VehicleTypeView from "@/ui/common-components/vehicle-type/vehicle-type";
import VehicleYear from "@/ui/common-components/vehicle-year/vehicle-year";

interface IOfferPanel {
  vehicle: Vehicle;
  siteData: ClientSiteData;
  isSmartphone: boolean;
  translations: Translations;
}

const OfferPanel: FC<IOfferPanel> = ({ vehicle, translations, siteData, isSmartphone }) => {
  const t_common = getTranslationModule(translations, "common");
  const isMobile = useIsMobile(768, isSmartphone);

  const vehicleStatus =
    vehicle.type === VehicleType.NEW
      ? vehicle.transitStatus && vehicle.transitStatus !== TransitStatus.IN_STOCK
        ? t_common(vehicle.transitStatus)
        : t_common(vehicle.type)
      : t_common(vehicle.type);

  const image = vehicle?.photosSmall && vehicle?.photosSmall[0];

  const dealerPhoto =
    siteData?.noImagesPhoto === "" || siteData?.noImagesPhoto === undefined
      ? ""
      : siteData?.baseUrl + siteData?.noImagesPhoto;

  const handleOnImageLoadError = (e: any, item: Vehicle) => {
    e.target.src = `/images/brands/${item?.make?.toLowerCase()}.png` || "/images/out-of-stock.png";
  };

  const likedVehicles = useLikedVehicles();
  const isLiked = likedVehicles.some((item: string) => item === vehicle.id);
  const compareList = useCompareVehicles();
  const isChecked = compareList.some((item) => vehicle.id === item);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.centeredContainer}>
          <div className={styles.wrapper}>
            <BackButton translations={translations} className={styles.goBack} />
            {!isMobile && (
              <div
                className={cx(styles.image, {
                  [styles.imageLogo]: image === undefined && dealerPhoto === "",
                  [styles.dealerPhoto]: dealerPhoto && !image,
                })}
              >
                <img
                  src={image || dealerPhoto || `/images/brands/${vehicle?.make?.toLowerCase()}.png`}
                  alt="car"
                  onError={(e) => handleOnImageLoadError(e, vehicle)}
                />
              </div>
            )}
            <div className={styles.info}>
              <div className={styles.infoYearType}>
                <VehicleTypeView vehicle={vehicle} vehicleStatus={vehicleStatus} />
                <VehicleYear year={vehicle.year} />
              </div>
              <h1
                title={`${vehicle.make} ${vehicle.model} ${vehicle.trim} ${vehicle.drivetrain} ${vehicle.transmission}`}
                className={styles.infoTitle}
              >
                {isMobile ? (
                  <>
                    {`${vehicle.make} ${vehicle.model} ${vehicle.trim}`}{" "}
                    <span className={styles.gray}>{`${vehicle.drivetrain} ${vehicle.transmission}`}</span>
                  </>
                ) : (
                  <>
                    {`${vehicle.make} ${vehicle.model} ${vehicle.trim}`}
                    <span className={styles.del}></span>
                    <span className={styles.text}>{vehicle.drivetrain}</span>
                    <span className={styles.del}></span>
                    <span className={styles.text}>{vehicle.transmission}</span>
                  </>
                )}
              </h1>
            </div>
          </div>
          <div className={styles.actions}>
            {isMobile ? (
              <>
                <CarFavoriteButton
                  className={cx(styles.favorite, {
                    [styles.favoriteLiked]: isLiked,
                  })}
                  vehicle={vehicle}
                  translations={translations}
                />
                <CarCompareCheckbox
                  vehicle={vehicle}
                  className={cx(styles.checkbox, {
                    [styles.checked]: isChecked,
                  })}
                  translations={translations}
                />
              </>
            ) : (
              <>
                <CarCompareCheckbox
                  vehicle={vehicle}
                  className={cx(styles.checkbox, {
                    [styles.checked]: isChecked,
                  })}
                  translations={translations}
                />
                <CarFavoriteButton
                  className={cx(styles.favorite, {
                    [styles.favoriteLiked]: isLiked,
                  })}
                  vehicle={vehicle}
                  translations={translations}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default OfferPanel;
